import { useEffect, useState } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, FileText, WarningCircle, CurrencyCircleDollar, CheckSquare, Notebook, ChartBar, IdentificationCard } from '@phosphor-icons/react';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [workerStats, setWorkerStats] = useState([]);
  const [pdvStats, setPdvStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchWorkerStats();
    fetchPdvStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      toast.error('Greška pri učitavanju statistike');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkerStats = async () => {
    try {
      const response = await api.get('/dashboard/worker-stats');
      setWorkerStats(response.data);
    } catch {}
  };

  const fetchPdvStats = async () => {
    try {
      const r = await api.get('/dashboard/pdv-stats');
      setPdvStats(r.data);
    } catch {}
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <p className="text-slate-500">Učitavanje...</p>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Ukupno klijenata',
      value: stats?.ukupno_klijenata || 0,
      icon: Users,
      color: 'bg-blue-50 text-blue-700 border-blue-200',
      testid: 'stat-total-clients'
    },
    {
      title: 'Aktivni klijenti',
      value: stats?.aktivni_klijenti || 0,
      icon: Users,
      color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      testid: 'stat-active-clients'
    },
    {
      title: 'PDV nije predat',
      value: stats?.pdv_nepredat || 0,
      icon: FileText,
      color: 'bg-amber-50 text-amber-700 border-amber-200',
      testid: 'stat-vat-pending'
    },
    {
      title: 'Fizička lica',
      value: stats?.fizicka_lica || 0,
      icon: IdentificationCard,
      color: 'bg-rose-50 text-rose-700 border-rose-200',
      testid: 'stat-fizicka-lica'
    },
    {
      title: 'Zadaci za danas',
      value: stats?.zadaci_danas || 0,
      icon: CheckSquare,
      color: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      testid: 'stat-tasks-today'
    },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card
              key={stat.testid}
              className="bg-white border border-slate-200 rounded-md shadow-sm hover:shadow-md transition-shadow"
              data-testid={stat.testid}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-xs font-semibold tracking-wider uppercase text-slate-500">
                  {stat.title}
                </CardTitle>
                <div className={`p-2 rounded-md border ${stat.color}`}>
                  <Icon size={20} weight="duotone" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-semibold text-slate-900" data-testid={`${stat.testid}-value`}>
                  {stat.value}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {stats?.pdv_nepredat > 0 && (
        <Card className="bg-amber-50 border border-amber-200 rounded-md" data-testid="dashboard-alert-vat">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <WarningCircle size={24} weight="duotone" className="text-amber-700 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-900">
                  Imate {stats.pdv_nepredat} nepredatih PDV prijava za tekući mjesec
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  Provjerite PDV praćenje i završite prijave na vrijeme.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {pdvStats && pdvStats.ukupno > 0 && (
        <Card className="overflow-hidden" data-testid="pdv-stats-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <ChartBar size={18} weight="duotone" className="text-indigo-600" />
              PDV statistike
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-center">
                <p className="text-2xl font-bold text-slate-800">{pdvStats.ukupno}</p>
                <p className="text-xs text-slate-500 mt-1">Ukupno</p>
              </div>
              <div className="p-3 rounded-lg bg-green-600 text-center">
                <p className="text-2xl font-bold text-white">{pdvStats.zavrseno}</p>
                <p className="text-xs text-green-100 mt-1">Završeno</p>
              </div>
              <div className="p-3 rounded-lg bg-red-700 text-center">
                <p className="text-2xl font-bold text-white">{pdvStats.unijeto}</p>
                <p className="text-xs text-red-100 mt-1">Uneseno</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-700 text-center">
                <p className="text-2xl font-bold text-white">{pdvStats.u_obradi}</p>
                <p className="text-xs text-blue-100 mt-1">U obradi</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {workerStats && workerStats.length > 0 && (
        <Card className="overflow-hidden" data-testid="worker-stats-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Notebook size={18} weight="duotone" className="text-blue-600" />
              Aktivnost radnika (Dnevnik rada)
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-2">
              {workerStats.map((w, i) => {
                const maxCount = workerStats[0]?.broj_zapisa || 1;
                const pct = Math.round((w.broj_zapisa / maxCount) * 100);
                return (
                  <div key={i} className="flex items-center gap-3" data-testid={`worker-stat-${i}`}>
                    <span className="text-sm font-medium text-slate-700 w-40 truncate">{w.radnik}</span>
                    <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${pct}%` }}></div>
                    </div>
                    <span className="text-sm font-bold text-slate-800 w-16 text-right">{w.broj_zapisa}</span>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-slate-400 mt-3">Ukupno zapisa: {workerStats.reduce((s, w) => s + w.broj_zapisa, 0)}</p>
          </CardContent>
        </Card>
      )}

    </div>
  );
}
